import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Region } from 'services/api';
import type { RegionsState } from './types';
import {
  getRegionsListAction,
  createRegionAction,
  updateRegionAction,
  removeRegionAction,
} from './actions';

const initialState: RegionsState = {
  regions: [],
  total: 0,
  selectedRegion: null,
  loading: false,
};

const regionsSlice = createSlice({
  name: 'regions',
  initialState,
  reducers: {
    setSelectedRegion: (state, action: PayloadAction<Region>) => {
      state.selectedRegion = action.payload;
    },
    clearSelectedRegion: (state) => {
      state.selectedRegion = null;
    },
    clearState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Get regions list
    builder.addCase(getRegionsListAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getRegionsListAction.fulfilled, (state, action) => {
      state.loading = false;
      state.regions = action.payload.content;
      state.total = action.payload.totalElements;
    });
    builder.addCase(getRegionsListAction.rejected, (state) => {
      state.loading = false;
    });

    // Create region
    builder.addCase(createRegionAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createRegionAction.fulfilled, (state, action) => {
      state.loading = false;
      state.regions = [action.payload, ...state.regions];
      state.total += 1;
    });
    builder.addCase(createRegionAction.rejected, (state) => {
      state.loading = false;
    });

    // Update region
    builder.addCase(updateRegionAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateRegionAction.fulfilled, (state, action) => {
      state.loading = false;
      state.regions = state.regions.map((region) =>
        region.id === action.payload.id ? action.payload : region,
      );
      state.selectedRegion = null;
    });
    builder.addCase(updateRegionAction.rejected, (state) => {
      state.loading = false;
    });

    // Remove region
    builder.addCase(removeRegionAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(removeRegionAction.fulfilled, (state, action) => {
      state.loading = false;
      state.regions = state.regions.filter((region) => region.id !== action.payload);
      state.total -= 1;
      state.selectedRegion = null;
    });
    builder.addCase(removeRegionAction.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const regionsReducer = regionsSlice.reducer;
export const { setSelectedRegion, clearSelectedRegion, clearState } = regionsSlice.actions;
